import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  forwardRef,
  input,
  InputSignal,
  signal,
  viewChild,
  WritableSignal
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { NgClass } from '@angular/common';
import { NgxMaskDirective, provideNgxMask } from 'ngx-mask';

@Component({
  selector: 'app-editable-row',
  imports: [NgClass, NgxMaskDirective],
  templateUrl: './editable-row.component.html',
  styleUrl: './editable-row.component.scss',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => EditableRowComponent),
      multi: true
    },
    provideNgxMask()
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class EditableRowComponent implements ControlValueAccessor {
  icon: InputSignal<string | undefined> = input<string | undefined>('');
  placeholder: InputSignal<string | undefined> = input<string | undefined>('');
  type: InputSignal<string> = input<string>('text');
  display: InputSignal<string | undefined> = input<string | undefined>(
    undefined
  );
  autocomplete: InputSignal<string | undefined> = input<string | undefined>(
    undefined
  );
  normalizeValue: InputSignal<((value: string) => string) | undefined> = input<
    ((value: string) => string) | undefined
  >(undefined);
  mask: InputSignal<string | undefined> = input<string | undefined>(undefined);
  showMaskTyped: InputSignal<boolean> = input<boolean>(false);
  hiddenInput: InputSignal<boolean> = input<boolean>(false);

  value: WritableSignal<string> = signal<string>('');
  isDisabled: WritableSignal<boolean> = signal<boolean>(false);
  isFocused: WritableSignal<boolean> = signal<boolean>(false);

  inputElement = viewChild<ElementRef>('inputElement');

  onContainerClick(): void {
    if (!this.isDisabled() && !this.isFocused()) {
      this.inputElement()?.nativeElement.focus();
    }
  }

  onInputFocus(): void {
    this.isFocused.set(true);
  }

  onInputClick(): void {
    // Trigger autocomplete on click
    const input = this.inputElement()?.nativeElement;
    if (input) {
      setTimeout(() => {
        // Dispatch mousedown event to trigger autocomplete
        const mousedownEvent = new MouseEvent('mousedown', {
          bubbles: true,
          cancelable: true,
          view: window
        });
        input.dispatchEvent(mousedownEvent);
      }, 10);
    }
  }

  onValueChange(event: Event): void {
    const eventTarget: HTMLInputElement = event.target as HTMLInputElement;
    let newValue = eventTarget.value;
    
    // Apply normalization if provided
    const normalizer = this.normalizeValue();
    if (normalizer) {
      newValue = normalizer(newValue);
      // Update the input element with normalized value
      eventTarget.value = newValue;
    }
    
    this.value.set(newValue);
    this.onChange(newValue);
  }

  onBlur(): void {
    this.isFocused.set(false);
    this.onTouched();
  }

  onKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Enter') {
      this.isFocused.set(false);
      this.inputElement()?.nativeElement.blur();
      this.onTouched();
    } else if (event.key === 'Escape') {
      this.isFocused.set(false);
      this.inputElement()?.nativeElement.blur();
    } else if (event.key === 'Tab') {
      // Allow tab to move to next field
      this.isFocused.set(false);
      this.onTouched();
    }
  }

  // ControlValueAccessor
  private onChange = (value: string) => {};
  private onTouched = () => {};

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.isDisabled.set(isDisabled);
  }

  writeValue(outsideValue: string): void {
    this.value.set(outsideValue || '');
  }
}
